import React, { useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Quote, Undo2, Redo2 } from "lucide-react";
import { otNoPaste } from "@/lib/otPasteGuard";

// THE WRITING EDITOR (My Writing — existing paragraphs AND "Write the next paragraph"; Structure view prose editing). A writing
// environment, not a chat box and not a word processor: Enter = an ordinary writing break; Cmd/Ctrl+Enter = Save now; Escape = Cancel.
// GOVERNING DATA RULE: the student's CANONICAL WRITING stays PLAIN TEXT (`text`, derived here from the editor content and saved through the
// existing save pathway); FORMATTING is PRESENTATIONAL METADATA (`formatting`: a sanitized allowed-subset HTML string saved separately). TWS
// analysis never reads formatting; formatting is never evidence of thesis / heading / part / importance. Paste stays blocked (otNoPaste).

const ALLOWED = new Set(["B", "STRONG", "I", "EM", "U", "UL", "OL", "LI", "BLOCKQUOTE", "BR", "P", "DIV"]);

export function sanitizeFormatting(html) {
  if (!html || typeof document === "undefined") return "";
  const root = document.createElement("div");
  root.innerHTML = html;
  const walk = (node) => {
    Array.from(node.childNodes).forEach((c) => {
      if (c.nodeType === 3) return;                                      // text
      if (c.nodeType !== 1) { c.remove(); return; }                      // comments etc.
      if (c.tagName === "SCRIPT" || c.tagName === "STYLE") { c.remove(); return; }
      walk(c);
      if (!ALLOWED.has(c.tagName)) {                                     // unwrap: keep the words, drop the tag
        while (c.firstChild) node.insertBefore(c.firstChild, c);
        c.remove();
        return;
      }
      Array.from(c.attributes).forEach((a) => c.removeAttribute(a.name));   // no attributes, no styles
    });
  };
  walk(root);
  return root.innerHTML;
}

// canonical plain text: block boundaries and <br> become newlines; everything else is the words themselves
export function plainTextOf(html) {
  if (!html) return "";
  const s = sanitizeFormatting(html)
    .replace(/<br>/gi, "\n")
    .replace(/<\/(p|div|li|blockquote)>/gi, "\n")
    .replace(/<[^>]+>/g, "");
  const t = document.createElement("textarea");
  t.innerHTML = s;
  return t.value.replace(/\u00a0/g, " ").replace(/\n{3,}/g, "\n\n").trim();
}

export const hasFormatting = (html) => /<(b|strong|i|em|u|ul|ol|li|blockquote)\b/i.test(html || "");
const escapeHtml = (t) => (t || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
export const htmlForText = (text) => escapeHtml(text).split("\n").map((l) => `<div>${l || "<br>"}</div>`).join("");
// what the editor OPENS with: the persisted formatting when it re-renders the text exactly, else the plain text as blocks (one rule, shared
// with any mirror that must lay out the same words the same way)
export const editorHtmlFor = (text, formatting) =>
  (formatting && plainTextOf(formatting) === (text || "").trim() ? sanitizeFormatting(formatting) : htmlForText(text));
// the prose metrics of the editable surface — a mirror/backdrop must use exactly these so highlights land on the words
export const EDITOR_TEXT_CLASS = "text-[15px] leading-[1.75] px-3.5 py-3 whitespace-pre-wrap";

const exec = (cmd) => { try { if (document.execCommand) document.execCommand(cmd, false, null); } catch { /* ignore */ } };

const TOOLS = [
  { key: "bold", label: "Bold", Icon: Bold, cmd: "bold" },
  { key: "italic", label: "Italic", Icon: Italic, cmd: "italic" },
  { key: "underline", label: "Underline", Icon: Underline, cmd: "underline" },
  { key: "bullets", label: "Bulleted list", Icon: List, cmd: "insertUnorderedList" },
  { key: "numbers", label: "Numbered list", Icon: ListOrdered, cmd: "insertOrderedList" },
  { key: "quote", label: "Quote", Icon: Quote, cmd: "formatBlock", arg: "blockquote" },
  { key: "undo", label: "Undo", Icon: Undo2, cmd: "undo" },
  { key: "redo", label: "Redo", Icon: Redo2, cmd: "redo" },
];

export function WritingEditor({ testid, initialText = "", initialFormatting = "", onChange, onSave, onCancel, onBlur, onScroll, backdrop = null, autoFocus = true, minHeightPx = 120, placeholder = "", allowPaste = false }) {
  const ref = useRef(null);
  const [active, setActive] = useState({});
  useEffect(() => {                                                     // opened once with the persisted state (formatting wins when it re-renders the text)
    if (!ref.current) return;
    ref.current.innerHTML = editorHtmlFor(initialText, initialFormatting);
    // `value` mirrors a textarea for tests / tooling: always the canonical plain text of what the editor shows
    try { Object.defineProperty(ref.current, "value", { get: () => plainTextOf(ref.current ? ref.current.innerHTML : ""), configurable: true }); } catch { /* ignore */ }
    if (autoFocus) ref.current.focus();
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const emit = () => {
    const html = sanitizeFormatting(ref.current ? ref.current.innerHTML : "");
    // plain writing carries NO formatting metadata; `html` is the whole sanitized surface (for a mirror that must lay out the same words)
    onChange && onChange({ text: plainTextOf(html), formatting: hasFormatting(html) ? html : "", html });
  };
  const refreshActive = () => {
    try {
      setActive({ bold: document.queryCommandState("bold"), italic: document.queryCommandState("italic"), underline: document.queryCommandState("underline"),
                  bullets: document.queryCommandState("insertUnorderedList"), numbers: document.queryCommandState("insertOrderedList") });
    } catch { /* ignore */ }
  };
  const apply = (tool) => (e) => {
    e.preventDefault();                                                  // keep the student's selection and cursor
    if (tool.cmd === "formatBlock") { try { document.execCommand("formatBlock", false, "blockquote"); } catch { /* ignore */ } }
    else exec(tool.cmd);
    emit(); refreshActive();
  };
  const keys = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onCancel && onCancel(); return; }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSave && onSave(); return; }
    // Enter alone: an ordinary writing break (the browser's own behaviour) — never a save. Cmd/Ctrl+B / I / Z stay the browser's.
  };
  return (
    <div data-testid={`${testid}-box`} className="rounded-md border border-[#8C3A2A]/40 focus-within:border-[#8C3A2A] bg-white">
      <div role="toolbar" aria-label="Formatting" data-testid={`${testid}-toolbar`} className="flex items-center gap-0.5 border-b border-stone-100 px-1.5 py-1">
        {TOOLS.map((t, k) => (
          <React.Fragment key={t.key}>
            {(k === 3 || k === 6) ? <span aria-hidden className="mx-1 h-4 w-px bg-stone-200" /> : null}
            <button type="button" data-testid={`${testid}-tool-${t.key}`} aria-label={t.label} title={t.label} aria-pressed={active[t.key] ? "true" : undefined}
              onMouseDown={apply(t)}
              className={`rounded p-1 text-stone-500 hover:bg-stone-100 hover:text-stone-800 ${active[t.key] ? "bg-stone-100 text-[#8C3A2A]" : ""}`}>
              <t.Icon className="h-3.5 w-3.5" />
            </button>
          </React.Fragment>
        ))}
      </div>
      <div className="relative">
        {/* an optional BACKDROP (e.g. a highlight mirror) sits under the editable surface: same box, same metrics, never inside contentEditable */}
        {backdrop}
        <div ref={ref} contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" data-testid={testid} data-placeholder={placeholder}
          {...(allowPaste ? {} : otNoPaste)} onInput={emit} onKeyDown={keys} onKeyUp={refreshActive} onMouseUp={refreshActive} onBlur={() => { emit(); onBlur && onBlur(); }} onScroll={onScroll}
          style={{ minHeight: minHeightPx }}
          className={`writing-editor relative block w-full min-w-0 max-h-[60vh] overflow-y-auto ${EDITOR_TEXT_CLASS} text-stone-900 outline-none bg-transparent`} />
      </div>
    </div>
  );
}
